import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import AppointmentSuccess from "@/components/AppointmentSuccess";
import PaymentRetryDialog from "@/components/PaymentRetryDialog";
import Loader from "@/components/Loader";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchBookedAppointmentDetail,
  payment,
  resetPayment,
  setBookingId,
  setPaymentRedirected,
  setPaymentStatus,
} from "@/store/slices/patient/payment";
import { config, setIsShowUniversalSearch } from "@/store/slices/config";

import { IAppointmentPaymentStatus } from "@/models/patient";
import { IDoctorScheduleConsultationType } from "@/models/doctor";

import { timeInAmPmFormat, transformDateToDDMMYYYY } from "@/utils";
import { initiatePaymentApi } from "@/utils/apis/patient/doctor";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

const PaymentSuccessOrFailure: React.FC = () => {
  const [retryDialogOpen, setRetryDialogOpen] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  const appointmentPaymentStatus = segments[segments.length - 1];

  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    paymentStatus,
    bookedAppointmentDetail,
    isPaymentRedirected,
    bookingId: failureBookingId,
    isLoading,
  } = useAppSelector(payment);
  const { isShowUniversalSearch } = useAppSelector(config);

  const {
    consultationType,
    slotNo,
    doctorName,
    experienceYears,
    slotDate,
    slotEndTime,
    slotStartTime,
    speciality,
    degree,
  } = bookedAppointmentDetail || {};

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const isSmallScreen = useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  const goToHomeOrCloseHandler = () => {
    navigate("/home");
    dispatch(resetPayment());
    if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
    }
  };

  const handleRetryPayment = async () => {
    try {
      setIsProcessing(true);
      const initiatePaymentResponse = await initiatePaymentApi({
        bookingId: failureBookingId,
      });
      if ("result" in initiatePaymentResponse) {
        document.open();
        document.write(initiatePaymentResponse?.result);
        document.close();
      }
      setIsProcessing(false);
      setRetryDialogOpen(false);
    } catch (error) {
      setErrorMessage(error.message);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (bookingId && appointmentPaymentStatus && !isPaymentRedirected) {
      dispatch(setPaymentStatus(appointmentPaymentStatus));
      dispatch(fetchBookedAppointmentDetail({ bookingId, isDetailed: true }));
      dispatch(setPaymentRedirected(true));
      dispatch(setBookingId(bookingId));
      navigate("/find-doctor", { state: { navItem: 1 } });
    }
  }, [bookingId, appointmentPaymentStatus]);

  if (isLoading && isPaymentRedirected) {
    return <Loader />;
  }
  return (
    <>
      {isPaymentRedirected &&
      paymentStatus === IAppointmentPaymentStatus.SUCCESS ? (
        <AppointmentSuccess
          isSmallScreen={isSmallScreen}
          dateSlotTime={
            consultationType === IDoctorScheduleConsultationType.VIDEO_CALL
              ? `${timeInAmPmFormat(slotStartTime)}, ${transformDateToDDMMYYYY(
                  slotDate
                )}`
              : `${timeInAmPmFormat(
                  slotStartTime
                )} - ${slotEndTime}, ${transformDateToDDMMYYYY(slotDate)}`
          }
          experienceYears={experienceYears}
          name={doctorName}
          onGoToHome={goToHomeOrCloseHandler}
          qualificationDetails={degree
            ?.split(",")
            ?.map((degree) => ({ degree_name: degree }))}
          speciality={speciality}
          type={consultationType}
          slotNo={slotNo}
        />
      ) : (
        <PaymentRetryDialog
          open={retryDialogOpen}
          onClose={() => setRetryDialogOpen(false)}
          onRetry={handleRetryPayment}
          errorMessage={errorMessage}
          isProcessing={isProcessing}
        />
      )}
    </>
  );
};

export default PaymentSuccessOrFailure;
